import { Plus } from 'lucide-react';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
} from '../ui/input-group';
import { formatHolds, parseHolds, type Holds } from './lead-holds';
import { cn } from '../../lib/utils';

interface HoldsCountInputProps {
  /** Human format: "17+", "17", or empty. */
  value: string;
  onChange: (value: string) => void;
  /**
   * The holds on the route — the ceiling, and the hold at which a "+" stops
   * meaning anything. Null on a route nobody has counted, which leaves the
   * field unbounded (the round will not have started in that state anyway).
   */
  max?: number | null;
  id?: string;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * How far up a lead route somebody got: a number, and a "+" when they made a
 * controlled move out of that hold.
 *
 * One control, not two: the plus is a toggle inside the field's own box, at the
 * end where the judge's thumb already is (`InputGroupAddon align="inline-end"`).
 * It reads as part of the number because that is what it is — "17+" is a value
 * between 17 and 18, and it is stored as 17.5 (see `LeadRoundResult`).
 *
 * **The top hold takes no plus.** A move out of the last hold is a move out of
 * nothing, so the toggle goes dead there and clears itself if it was on — the
 * same rule the server refuses the card by.
 */
export function HoldsCountInput({
  value,
  onChange,
  max,
  id,
  disabled,
  invalid,
  className,
  'data-testid': testId,
}: HoldsCountInputProps) {
  const { count, plus } = parseHolds(value);

  // At the top of the route there is nothing to move out of.
  const atTop = max != null && count !== '' && Number(count) >= max;
  const plusAllowed = !atTop;

  const emit = (next: Holds) => onChange(formatHolds(next));

  const handleCount = (raw: string) => {
    // Only digits, and never past the last hold — the ceiling is enforced here
    // rather than complained about afterwards.
    const digits = raw.replace(/\D/g, '');
    if (digits === '') return emit({ count: '', plus: false });

    const clamped = max != null ? Math.min(Number(digits), max) : Number(digits);
    emit({ count: String(clamped), plus: plus && !(max != null && clamped >= max) });
  };

  return (
    <InputGroup
      className={cn('w-full', invalid && 'border-destructive', className)}
      aria-invalid={invalid || undefined}
      data-testid={testId}
    >
      <InputGroupInput
        id={id}
        type="text"
        inputMode="numeric"
        value={count}
        disabled={disabled}
        onChange={(event) => handleCount(event.target.value)}
        placeholder="0"
        className="text-right"
        data-testid={testId ? `${testId}-count` : 'holds-count'}
      />

      <InputGroupAddon align="inline-end">
        {/* How many there are to get, so the number above is read against
            something. Silent on a route nobody has counted. */}
        {max != null && <InputGroupText className="text-xs">/ {max}</InputGroupText>}
        <InputGroupButton
          aria-pressed={plus}
          aria-label="+"
          disabled={disabled || !plusAllowed || count === ''}
          onClick={() => emit({ count, plus: !plus })}
          data-testid={testId ? `${testId}-plus` : 'holds-plus'}
        >
          <Plus className="size-4" aria-hidden />
        </InputGroupButton>
      </InputGroupAddon>
    </InputGroup>
  );
}
